import { SeoMapDto, SeoQuestionDto } from "@growth/dto";
import { Badge, Button, cn } from "@growth/ui";
import { LoaderCircle, PenLine, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { formatRelative } from "../lib/format";
import { useBriefQuestion, useRefreshSeo, useSeoMap } from "../lib/seo";

const COVERAGE: Record<string, { label: string; className: string }> = {
  strong: { label: "Strong", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  thin: { label: "Thin", className: "" },
  gap: { label: "Gap", className: "bg-amber-50 text-amber-800 ring-amber-200" },
};

/** What the audience searches, how well the blog answers it, and briefing articles for the gaps. */
export const SeoPage = () => {
  const { brand, atLeast } = useActive();
  const map = useSeoMap();
  const refresh = useRefreshSeo();
  const running = map.data?.run && ["queued", "running"].includes(map.data.run.status);

  return (
    <section className="max-w-6xl space-y-6">
      <PageHeader
        eyebrow={brand?.name ?? ""}
        title="SEO and blog"
        actions={
          atLeast("EDITOR") &&
          map.data?.hasSite && (
            <Button
              disabled={refresh.isPending || Boolean(running)}
              onClick={() => refresh.mutate()}
            >
              {refresh.isPending || running ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              {map.data.topics.length ? "Map again" : "Map what people search"}
            </Button>
          )
        }
      >
        The questions people search for, and how well the blog answers each. Briefed articles are
        written with the brand's claims and voice, and land in Blog posts for a person to publish.
      </PageHeader>
      {(map.error ?? refresh.error) && (
        <ErrorText>{apiErrorMessage(map.error ?? refresh.error)}</ErrorText>
      )}
      {map.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {map.data && <Body map={map.data} running={Boolean(running)} />}
    </section>
  );
};

const Body = ({ map, running }: { map: SeoMapDto; running: boolean }) => {
  if (!map.hasSite) {
    return (
      <p className="rounded-xl border border-line bg-white p-6 text-sm text-muted">
        This brand has no blog yet. Set one up in{" "}
        <Link to="/blog-settings" className="text-accent underline">
          Blog settings
        </Link>
        , then map what people search.
      </p>
    );
  }
  const total = map.topics.reduce((sum, topic) => sum + topic.questions.length, 0);
  const strong = map.topics.reduce((sum, topic) => sum + topic.counts.strong, 0);
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted">
        {map.publishedPosts} published post{map.publishedPosts === 1 ? "" : "s"} on{" "}
        <a href={map.blogUrl!} target="_blank" rel="noreferrer" className="text-accent underline">
          {map.blogUrl!.replace("https://", "")}
        </a>
        {map.run && (
          <>
            {" · "}
            {running
              ? "the SEO strategist is searching (a few minutes)…"
              : map.run.status === "failed"
                ? `the last map stopped: ${map.run.error}`
                : `mapped ${formatRelative(map.run.endedAt ?? map.run.startedAt ?? "")} · US$${map.run.costUsd.toFixed(2)}`}
          </>
        )}
        {total > 0 && ` · ${strong} of ${total} questions answered well`}
      </p>
      {map.topics.length === 0 && !running && (
        <p className="rounded-xl border border-line bg-white p-6 text-sm text-muted">
          No map yet. The SEO strategist searches what this audience asks and checks each question
          against the published posts. It counts toward the weekly research budget.
        </p>
      )}
      <div className="grid gap-3 md:grid-cols-3">
        {map.topics.map((topic) => {
          const count = topic.questions.length || 1;
          return (
            <a
              key={topic.id}
              href={`#topic-${topic.id}`}
              className="space-y-2 rounded-xl border border-line bg-white p-4 hover:border-accent"
            >
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted">
                Topic
              </p>
              <p className="font-serif text-lg leading-snug text-ink">{topic.name}</p>
              <div className="flex h-2 overflow-hidden rounded-full bg-paper" aria-hidden>
                <span
                  className="bg-emerald-600"
                  style={{ width: `${(topic.counts.strong / count) * 100}%` }}
                />
                <span
                  className="bg-accent/40"
                  style={{ width: `${(topic.counts.thin / count) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted">
                {topic.counts.strong} strong · {topic.counts.thin} thin · {topic.counts.gap} gap
              </p>
            </a>
          );
        })}
      </div>
      {map.topics.map((topic) => (
        <section
          key={topic.id}
          id={`topic-${topic.id}`}
          className="overflow-hidden rounded-xl border border-line bg-white"
        >
          <div className="border-b border-line p-4">
            <h2 className="font-serif text-xl text-ink">{topic.name}</h2>
            {topic.summary && <p className="text-sm text-muted">{topic.summary}</p>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-muted">
                  <th scope="col" className="px-4 py-2 font-semibold">
                    Question
                  </th>
                  <th scope="col" className="px-2 py-2 font-semibold">
                    Our page
                  </th>
                  <th scope="col" className="px-2 py-2 font-semibold">
                    How well
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-semibold">
                    Next
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {topic.questions.map((question) => (
                  <Row key={question.id} question={question} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
};

const Row = ({ question }: { question: SeoQuestionDto }) => {
  const { atLeast } = useActive();
  const brief = useBriefQuestion();
  const coverage = COVERAGE[question.coverage] ?? COVERAGE["gap"]!;
  const writing = question.runStatus === "RUNNING";
  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <p className="text-ink">{question.question}</p>
        {question.note && <p className="mt-0.5 text-xs text-muted">{question.note}</p>}
        <p className="mt-0.5 text-xs">
          {question.sources.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="mr-2 text-accent underline"
            >
              {source.title}
            </a>
          ))}
        </p>
      </td>
      <td className="max-w-56 px-2 py-3">
        {question.post ? (
          <Link to={`/posts/${question.post.id}`} className="text-accent underline">
            {question.post.title}
          </Link>
        ) : (
          <span className="text-muted">None</span>
        )}
      </td>
      <td className="px-2 py-3">
        <Badge className={coverage.className}>{coverage.label}</Badge>
      </td>
      <td className="px-4 py-3 text-right">
        {question.generationRunId && (
          <Link
            to={`/ai/runs/${question.generationRunId}`}
            className={cn("block text-xs underline", writing ? "text-muted" : "text-accent")}
          >
            {writing
              ? "Writing…"
              : question.runStatus === "SUCCEEDED"
                ? "Draft ready"
                : "See the run"}
          </Link>
        )}
        {atLeast("EDITOR") && question.coverage !== "strong" && !writing && (
          <Button
            variant="secondary"
            className="whitespace-nowrap"
            disabled={brief.isPending}
            onClick={() => brief.mutate(question.id)}
          >
            {brief.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <PenLine className="size-4" />
            )}
            {question.coverage === "gap" ? "Brief an article" : "Brief an update"}
          </Button>
        )}
        {brief.isError && <ErrorText>{apiErrorMessage(brief.error)}</ErrorText>}
      </td>
    </tr>
  );
};
